import React, { Fragment, useMemo } from 'react';
import Head from 'next/head';
import { useTranslation, Locale } from '@rbx/intl';
import { logOnServer } from '@modules/utilities/utils/log';
import { resolveStaticSrc } from '@modules/ui/markdown/utils/staticLinkUtils';
import {
  availableDocsContentLocales,
  defaultLocale,
  LocaleHrefLangMap,
} from '@modules/localization/constants/translationResourceConstants';
import pagePreviewImageUrls from '@modules/utilities/constants/pagePreviewImageUrls';
import baseUrl from '@modules/utilities/constants/baseUrl';
import settings from '@modules/config/settings';
import { join } from '@modules/utilities/utils/path';
import { useUIThemeMeta } from '@rbx/ui';
import routerUtils from '@modules/utilities/utils/router';
import { useRouter } from 'next/router';
import useCurrentLocale from '@modules/localization/hooks/useCurrentLocale';
import { ArticleSchemaMarkupContext } from '@modules/seo/structured-data/types';
import { useAppConfig } from './AppConfigProvider';

export type TOpenGraphMeta = {
  locale: Locale;
  title: string;
  description: string;
  imageUrl: string;
  baseUrl: string;
};

type TAppHeadProps = {
  siteTitle?: string;
  pageMetaDescription?: string | null;
  slug?: string;
  showHrefLang?: boolean;
  openGraphMeta: Partial<TOpenGraphMeta>;
  seoStructureData?: ArticleSchemaMarkupContext;
};

const AppHead: React.FC<TAppHeadProps> = ({
  pageMetaDescription,
  siteTitle,
  slug,
  openGraphMeta,
  seoStructureData,
  showHrefLang = false,
}) => {
  const uiThemeMeta = useUIThemeMeta();
  const { translate } = useTranslation();
  const { subPath } = useCurrentLocale();
  const { blockSearchIndexing } = useAppConfig();
  const router = useRouter();

  const processedOpenGraphMeta = useMemo(() => {
    const pageDescription = openGraphMeta.description || pageMetaDescription;
    if (!pageDescription) {
      logOnServer(`Empty Page Description with title ${siteTitle}`);
    }
    return {
      locale: openGraphMeta.locale || defaultLocale,
      title: siteTitle,
      description: pageDescription,
      imageUrl: resolveStaticSrc(openGraphMeta.imageUrl || pagePreviewImageUrls.landing),
      baseUrl: openGraphMeta.baseUrl || baseUrl,
    };
  }, [
    pageMetaDescription,
    openGraphMeta.baseUrl,
    openGraphMeta.description,
    openGraphMeta.imageUrl,
    openGraphMeta.locale,
    siteTitle,
  ]);

  const pagePath = useMemo(() => {
    let pathName = routerUtils.getPathName(router);
    pathName = pathName.replace(/\/$/, '');
    return pathName;
  }, [router]);

  const noIndexSlugs = settings.noindexPageSlugsCSV.split(',');

  return (
    <Head>
      <title>{siteTitle}</title>
      {uiThemeMeta}
      {processedOpenGraphMeta.description && (
        <meta name='description' content={processedOpenGraphMeta.description} />
      )}
      {((slug && noIndexSlugs.includes(slug)) || blockSearchIndexing) && (
        <meta name='robots' content='noindex' />
      )}
      <meta property='og:url' content={processedOpenGraphMeta.baseUrl} key='og:url' />
      <meta property='og:title' content={processedOpenGraphMeta.title} key='og:title' />
      <meta
        property='og:description'
        content={processedOpenGraphMeta.description ?? translate('Description.LearnToCreate')}
        key='og:description'
      />
      <meta property='og:image' content={processedOpenGraphMeta.imageUrl} key='og:image' />
      <meta property='og:type' content='website' key='og:type' />
      <meta property='og:locale' content={processedOpenGraphMeta.locale} key='og:locale' />
      <meta property='twitter:title' content={processedOpenGraphMeta.title} key='twitter:title' />
      <meta
        property='twitter:description'
        content={processedOpenGraphMeta.description ?? translate('Description.LearnToCreate')}
        key='twitter:description'
      />
      <meta
        property='twitter:image'
        content={processedOpenGraphMeta.imageUrl}
        key='twitter:image'
      />
      <meta property='twitter:card' content='summary_large_image' key='twitter:card' />
      <meta property='twitter:site' content='@robloxdevrel' key='twitter:site' />
      <link rel='canonical' href={`${baseUrl}${pagePath}`} />
      {showHrefLang && (
        <Fragment>
          {availableDocsContentLocales.map((locale) => (
            <link
              key={locale}
              rel='alternate'
              href={join(baseUrl, locale.toLowerCase(), subPath)}
              hrefLang={LocaleHrefLangMap.get(locale)}
            />
          ))}
          <link rel='alternate' href={join(baseUrl, subPath)} hrefLang='x-default' />
        </Fragment>
      )}

      {seoStructureData && (
        <script
          type='application/ld+json'
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(seoStructureData) }}
        />
      )}
      {settings.hideDevErrors && (
        <style>{`
               body {
                 overflow-x: initial !important;
               }
               nextjs-portal {
                 display: none !important
               }
           `}</style>
      )}
    </Head>
  );
};

export default AppHead;
